// RolesVersion2 only. Shared table methods are in tableMixin.js.
// Methods here with the same name as a tableMixin method replace it on this page.
import axios from "axios";

export default {
    name: "RolesVersion2",
    methods: {
        /* ---------------- Permission picker: tick boxes ---------------- */
        isAvailablePermissionSelected(permissionId) {
            return !!this.selectedAvailablePermissions[permissionId];
        },
        isAssignedPermissionSelected(permissionId) {
            return !!this.selectedAssignedPermissions[permissionId];
        },
        isAvailablePermissionCategorySelected(permissions) {
            return permissions.length > 0 &&
                permissions.every((permission) => !!this.selectedAvailablePermissions[permission.id]);
        },
        toggleAvailablePermissionCategorySelection(permissions) {
            const allSelected = permissions.every((permission) => !!this.selectedAvailablePermissions[permission.id]);
            permissions.forEach((permission) => {
                this.selectedAvailablePermissions[permission.id] = !allSelected;
            });
        },
        toggleAvailablePermission(permission) {
            const id = permission.id;
            this.selectedAvailablePermissions[id] = !this.selectedAvailablePermissions[id];
        },
        toggleAssignedPermission(permission) {
            const id = permission.id;
            this.selectedAssignedPermissions[id] = !this.selectedAssignedPermissions[id];
        },
        isAssignedPermissionCategorySelected(permissions) {
            return permissions.length > 0 &&
                permissions.every((permission) => !!this.selectedAssignedPermissions[permission.id]);
        },
        toggleAssignedPermissionCategorySelection(permissions) {
            const allSelected = permissions.every((permission) => !!this.selectedAssignedPermissions[permission.id]);
            permissions.forEach((permission) => {
                this.selectedAssignedPermissions[permission.id] = !allSelected;
            });
        },
        setAvailableCategoryCheckboxState(element, permissions) {
            if (!element) {
                return;
            }
            const selectedCount = permissions.filter((permission) => !!this.selectedAvailablePermissions[permission.id]).length;
            element.indeterminate = selectedCount > 0 && selectedCount < permissions.length;
        },
        setAssignedCategoryCheckboxState(element, permissions) {
            if (!element) {
                return;
            }
            const selectedCount = permissions.filter((permission) => !!this.selectedAssignedPermissions[permission.id]).length;
            element.indeterminate = selectedCount > 0 && selectedCount < permissions.length;
        },

        /* ---------------- Permission picker: moving between the lists ---------------- */
        moveSelectedPermissionsToAssigned() {
            const selectedIds = Object.keys(this.selectedAvailablePermissions)
                .filter((id) => this.selectedAvailablePermissions[id])
                .map(Number);

            if (selectedIds.length === 0) {
                return;
            }

            const selectedPermissions = this.availablePermissions.filter((permission) =>
                selectedIds.includes(Number(permission.id))
            );

            selectedPermissions.forEach((permission) => {
                const exists = this.assignedPermissions.some((item) => Number(item.id) === Number(permission.id));
                if (!exists) {
                    this.assignedPermissions.push(permission);
                }
            });

            // Open the category of the first permission moved
            const firstMovedPermission = selectedPermissions[0];
            if (firstMovedPermission) {
                const categoryName = firstMovedPermission.permission_cat_name || "Other";
                this.expandedAssignedPermissionCategories = [categoryName];
            }

            this.availablePermissions = this.availablePermissions.filter((permission) =>
                !selectedIds.includes(Number(permission.id))
            );

            this.formInputs.permissions = [...this.assignedPermissions];

            this.selectedAvailablePermissions = {};
            this.selectedAssignedPermissions = {};
        },
        moveSelectedPermissionsToAvailable() {
            const selectedIds = Object.keys(this.selectedAssignedPermissions)
                .filter((id) => this.selectedAssignedPermissions[id])
                .map(Number);

            if (selectedIds.length === 0) {
                return;
            }

            const selectedPermissions = this.assignedPermissions.filter((permission) =>
                selectedIds.includes(Number(permission.id))
            );

            selectedPermissions.forEach((permission) => {
                const exists = this.availablePermissions.some((item) => Number(item.id) === Number(permission.id));
                if (!exists) {
                    this.availablePermissions.push(permission);
                }
            });

            // Open the category of the first permission moved
            const firstMovedPermission = selectedPermissions[0];
            if (firstMovedPermission) {
                const categoryName = firstMovedPermission.permission_cat_name || "Other";
                this.expandedAvailablePermissionCategories = [categoryName];
            }

            this.assignedPermissions = this.assignedPermissions.filter((permission) =>
                !selectedIds.includes(Number(permission.id))
            );

            this.formInputs.permissions = [...this.assignedPermissions];

            this.selectedAvailablePermissions = {};
            this.selectedAssignedPermissions = {};
        },

        /* ---------------- Permission picker: categories ---------------- */
        // Grouped by category, categories and names in A-Z order
        groupPermissionsByCategory(permissions) {
            const groups = {};

            permissions.forEach((permission) => {
                const categoryName = permission.permission_cat_name || "Other";
                if (!groups[categoryName]) {
                    groups[categoryName] = [];
                }
                groups[categoryName].push(permission);
            });

            return Object.keys(groups)
                .sort((a, b) => a.localeCompare(b))
                .reduce((sortedGroups, categoryName) => {
                    sortedGroups[categoryName] = groups[categoryName].sort((a, b) => a.name.localeCompare(b.name));
                    return sortedGroups;
                }, {});
        },
        toggleAvailablePermissionCategory(categoryName) {
            const index = this.expandedAvailablePermissionCategories.indexOf(categoryName);
            this.expandedAvailablePermissionCategories = index === -1 ? [categoryName] : [];
        },
        isAvailablePermissionCategoryExpanded(categoryName) {
            return this.expandedAvailablePermissionCategories.includes(categoryName);
        },
        toggleAssignedPermissionCategory(categoryName) {
            const index = this.expandedAssignedPermissionCategories.indexOf(categoryName);
            this.expandedAssignedPermissionCategories = index === -1 ? [categoryName] : [];
        },
        isAssignedPermissionCategoryExpanded(categoryName) {
            return this.expandedAssignedPermissionCategories.includes(categoryName);
        },

        /* ---------------- Form ---------------- */
        // Add button: empty form, nothing assigned, every permission on the Available side
        clearForm() {
            this.isEditing = false;
            this.isSaving = false;
            this.submitted = false;
            this.formInputs = this.getDefaultFormInputs();

            this.selectedAvailablePermissions = {};
            this.selectedAssignedPermissions = {};

            this.expandedAvailablePermissionCategories = [];
            this.expandedAssignedPermissionCategories = [];

            this.assignedPermissions = [];
            this.availablePermissions = [...this.permissionOptions];

            this.$clearValidationStates("#recordForm");
        },
        // Permissions loaded: fill both sides of the picker
        handleFetchPermissionsSuccess(response) {
            this.permissionOptions = response.data.results || [];

            const assignedIds = this.parsePermissions(this.formInputs.permissions);

            this.assignedPermissions = this.permissionOptions.filter((permission) =>
                assignedIds.includes(Number(permission.id))
            );

            this.availablePermissions = this.permissionOptions.filter((permission) =>
                !assignedIds.includes(Number(permission.id))
            );

            this.expandedAvailablePermissionCategories = [];
            this.expandedAssignedPermissionCategories = [];
        },

        /* ---------------- Delete ---------------- */
        // Delete one role: explains why a role can't be deleted
        deleteRecord(id) {
            this.$showOrHideLoader(this.tableData.loaderMessage);
            const data = { id, deleteType: this.tableData.deleteType, action: "deleteRecord" };

            axios
                .delete(this.apiUrl, this.$handleConfig(data))
                .then((response) => {
                    if (response.data.status == "success") {
                        this.handleDeleteRecordSuccess(response);
                    } else {
                        this.tableData.isVisible = false;
                        this.handleError("ending", "This role could not be deleted. It may be a system role, or some users still have it. Change those users' role first.", "danger", "page");
                    }
                })
                .catch((error) => this.handleRequestError(error, "page"));
        },
    },
};